/* eslint-disable react/no-array-index-key */
import {dialog} from '@/modules/layout'
import {Box, Collapsible, Text} from '@chakra-ui/react'
import {
  Batch,
  BatchQuantityError,
  ConsumableBatchType,
  DistributedListItem,
  PatientTherapySequence,
  RestVialItem,
  SubstanceBatchTypes,
  TradeName,
} from '@cypro/engine/schema'
import {withFieldGroup} from '@cypro/ui/integrations/form'
import {useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'
import {Decimal} from 'decimal.js'
import {useCallback, useEffect, useMemo} from 'react'
import {useGetSettings} from '@/modules/settings/api/use-get-settings'
import {useGetInactivePrescribedNames} from '@/modules/products/trade-names/api'
import {queries} from '@/shared/integrations/zero'
import {useQuery} from '@rocicorp/zero/react'
import {AddBatchDialog, AddBatchDialogProps} from '../../dialogs/add-batch-dialog'
import {SolventRequirementsList} from '../solvent-requirements-list'
import {
  buildRequiredConsumables,
  buildRequiredSolvents,
  buildSolventRequirements,
  findSolventsWithoutTradeName,
  getOrderCarrierContainer,
  getRestVialCoveredAmount,
  isCarrierUsageEnough,
  isUsageEnough,
  PrescriptionRequiredAmounts,
} from '../utils'
import {OrderCollapsibleTrigger} from './order-collapsible-trigger'
import {OrderSubstancesForm} from './order-substances-form'
import {ReturnRow} from './return-row'

function openAddBatchDialog(props: AddBatchDialogProps) {
  dialog.open('add-batch-dialog', {
    children: <AddBatchDialog {...props} />,
    contentProps: {minWidth: '50vw'},
  })
}

interface PickingListItemFormProps {
  index: number
  batches: Batch[]
  order: PatientTherapySequence
  batchErrors?: BatchQuantityError[]
  requiredAmounts: PrescriptionRequiredAmounts
  addBatch: (batchId: string) => void
  onRemoveBatch?: (batchId: string, category: SubstanceBatchTypes) => void
  onIsolateBatch?: (batchId: string, category: SubstanceBatchTypes) => void
  onBlur?: () => void
}

export const PickingListItemForm = withFieldGroup({
  defaultValues: {
    sequenceId: '',
    orderId: '',
    activeSubstances: {batches: []},
    carrier: {batches: []},
    solvents: {batches: []},
    consumables: {batches: []},
    restVials: [],
    returns: [],
    isValid: true,
  } as DistributedListItem,
  props: {} as PickingListItemFormProps,
  render: ({group, order, batches, onRemoveBatch, onIsolateBatch, addBatch, batchErrors, requiredAmounts, onBlur}) => {
    const {t} = useTranslate('common')
    const {settings} = useGetSettings()
    const skipSolventStockCheck = Boolean(settings?.skipSolventStockCheck)

    const {prescribedNameIds, requiredPrescribedNameAmounts, requiredCarrierAmount} = requiredAmounts
    const activeSubstanceBatches = useStore(group.store, state => state.values.activeSubstances.batches)
    const carrierBatches = useStore(group.store, state => state.values.carrier.batches)
    const solventBatches = useStore(group.store, state => state.values.solvents.batches)
    const consumableBatches = useStore(group.store, state => state.values.consumables.batches)
    const restVials = useStore(group.store, state => state.values.restVials)
    const returns = useStore(group.store, state => state.values.returns)
    const hasReturn = returns.length > 0
    // no stock can be added for an inactive trade name, so say that instead of "Insufficient amount" (CY-259)
    const inactiveTradeNames = useGetInactivePrescribedNames(prescribedNameIds)

    const buildAdjustedRequiredAmounts = useCallback(() => {
      return Object.fromEntries(
        Object.entries(requiredPrescribedNameAmounts).map(([id, val]) => {
          const covered = getRestVialCoveredAmount(restVials as RestVialItem[], batches, id, val.unit)
          return [id, {...val, amount: Decimal.max(val.amount.sub(covered), new Decimal(0))}]
        })
      )
    }, [requiredPrescribedNameAmounts, restVials, batches])

    const {
      sharedBatch,
      needReconstitution,
      allowedSolvents,
      solventRequirements,
      anyOutOfStock,
      activeSubstancesOutOfStock,
      carrierOutOfStock,
      carrierContainer,
      solventOutOfStock,
      consumableOutOfStock,
      hasRestVials,
    } = useMemo(() => {
      // only new vials take solvent: a rest vial is already a solution
      const {
        result: requiredSolvents,
        allowedSolvents,
        needReconstitution,
      } = buildRequiredSolvents(activeSubstanceBatches, batches)
      const solventRequirements = buildSolventRequirements(activeSubstanceBatches, batches)
      const requiredConsumables = buildRequiredConsumables(activeSubstanceBatches, batches)

      const adjustedRequiredAmounts = buildAdjustedRequiredAmounts()
      // a rest vial's source batch brings its trade name's empty bag or pump, like a new vial does
      const restVialBatchIds = (restVials as RestVialItem[]).flatMap(rv => (rv.batchId ? [rv.batchId] : []))
      const carrierContainer = getOrderCarrierContainer(
        order?.volumeMode,
        [...activeSubstanceBatches.map(b => b.batchId), ...restVialBatchIds],
        batches
      )

      const {
        hasSharedBatches,
        outOfStock: activeSubstancesOutOfStock,
        batchIds: activeSubstancesBatchIds,
      } = isUsageEnough(activeSubstanceBatches, batches, adjustedRequiredAmounts)
      const {
        hasSharedBatches: sharedCarrierBatch,
        outOfStock: carrierOutOfStock,
        batchIds: carrierBatchIds,
      } = isCarrierUsageEnough(
        carrierBatches,
        batches,
        order?.carrierSolutionId || '',
        requiredCarrierAmount,
        carrierContainer
      )
      const {
        hasSharedBatches: sharedSolventBatch,
        outOfStock: solventOutOfStock,
        batchIds: solventBatchIds,
      } = isUsageEnough(solventBatches, batches, requiredSolvents, 'solvents', allowedSolvents)

      const {
        hasSharedBatches: sharedConsumableBatch,
        outOfStock: consumableOutOfStock,
        batchIds: consumableBatchIds,
      } = isUsageEnough(consumableBatches, batches, requiredConsumables, 'consumables')

      // with the solvent stock check skipped, solvents never count as shared, missing or out of stock
      const checkSolvents = !skipSolventStockCheck
      const isShared =
        hasSharedBatches || sharedCarrierBatch || (checkSolvents && sharedSolventBatch) || sharedConsumableBatch
      const orderBatches = new Set([
        ...activeSubstancesBatchIds,
        ...carrierBatchIds,
        ...(checkSolvents ? solventBatchIds : []),
        ...consumableBatchIds,
      ])
      const stockOutage = batchErrors?.filter(e => orderBatches.has(e.batchId))
      const anyOutOfStock =
        activeSubstancesOutOfStock.length > 0 ||
        carrierOutOfStock.length > 0 ||
        (checkSolvents && solventOutOfStock.length > 0) ||
        consumableOutOfStock.length > 0 ||
        Boolean(stockOutage && stockOutage.length > 0)
      return {
        sharedBatch: isShared,
        needReconstitution,
        allowedSolvents,
        solventRequirements,
        anyOutOfStock,
        activeSubstancesOutOfStock,
        carrierOutOfStock,
        carrierContainer,
        solventOutOfStock,
        consumableOutOfStock,
        hasRestVials: restVials.length > 0,
      }
    }, [
      activeSubstanceBatches,
      carrierBatches,
      solventBatches,
      consumableBatches,
      order.carrierSolutionId,
      order.volumeMode,
      batchErrors,
      buildAdjustedRequiredAmounts,
      requiredCarrierAmount,
      batches,
      restVials,
      skipSolventStockCheck,
    ])

    // tells the user which bag size the carrier needs: one bag of at least the volume when the drug goes into a
    // pre-filled bag, any bags that together hold the volume when they only fill an empty container
    const carrierVolume = requiredCarrierAmount.amount
    const carrierHelperText =
      hasReturn || carrierVolume.lessThanOrEqualTo(0)
        ? undefined
        : carrierContainer === 'PREFILLED'
          ? t('carrier_needs_one_bag', 'Needs one {name} bag of at least {volume} ml', {
              name: requiredCarrierAmount.name,
              volume: carrierVolume.toString(),
            })
          : t('carrier_needs_bags', 'Needs {name} bags holding at least {volume} ml together', {
              name: requiredCarrierAmount.name,
              volume: carrierVolume.toString(),
            })

    const solventDrugIds = useMemo(() => [...new Set(allowedSolvents)], [allowedSolvents])
    // an empty substances filter would match every trade name, so query an id that cannot exist instead
    const [solventTradeNames, solventTradeNamesMeta] = useQuery(
      queries.products.listTradeNames({
        filters: {substances: solventDrugIds.length ? solventDrugIds : []},
        sort: {dir: 'asc', field: 'name'},
        populate: 'list',
      })
    )
    const solventsWithoutTradeName = useMemo(
      () =>
        solventTradeNamesMeta.type === 'complete'
          ? findSolventsWithoutTradeName(solventDrugIds, batches, solventTradeNames as unknown as TradeName[])
          : [],
      [solventDrugIds, batches, solventTradeNames, solventTradeNamesMeta.type]
    )

    const onValuesChange = () => {
      onBlur?.()
    }

    useEffect(() => {
      // A return already covers this order's active substance/carrier amounts, so batch shortages don't apply
      group.setFieldValue('isValid', hasReturn || !anyOutOfStock)
    }, [anyOutOfStock, hasReturn, group])

    function addTradeNameBatch(
      type: SubstanceBatchTypes | ConsumableBatchType,
      batchId: string,
      consumableId?: string
    ) {
      group.pushFieldValue(`${type}.batches`, {batchId, sharedWith: [], quantity: 1, consumableId})
      addBatch(batchId)
    }

    const carrierSolutionId = order?.carrierSolutionId
    return (
      <Collapsible.Root key={order?.id} bg="gray.50" borderRadius="md" borderWidth="1px">
        <OrderCollapsibleTrigger
          order={order}
          anyOutOfStock={hasReturn ? false : anyOutOfStock}
          needReconstitution={needReconstitution}
          sharedBatch={sharedBatch}
          hasRestVials={hasRestVials}
          hasReturn={hasReturn}
        />

        <Collapsible.Content padding="3">
          <Box display={'flex'} alignItems={'flex-start'} flexWrap={'wrap'} gap="5">
            {hasReturn && (
              <Box
                flex="1"
                display={'flex'}
                flexDirection={'column'}
                borderColor="border"
                borderWidth={'1px'}
                borderRadius="md"
                paddingRight="3"
                paddingLeft="1"
                paddingBlock="1"
                minWidth="28rem"
                maxWidth="28rem"
                bg="white"
              >
                <Box
                  display={'flex'}
                  gap={1}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                  paddingInline="2"
                  paddingBlock="1.5"
                >
                  <Text fontSize="sm" fontWeight="medium">
                    {t('pharmacy_return')}
                  </Text>
                </Box>
                {returns.map(ret => (
                  <ReturnRow
                    key={ret.returnId}
                    returnItem={ret}
                    onRemove={() => {
                      group.setFieldValue(
                        'returns',
                        returns.filter(r => r.returnId !== ret.returnId)
                      )
                      onValuesChange?.()
                    }}
                  />
                ))}
              </Box>
            )}
            <group.Field
              name="activeSubstances"
              listeners={{
                onMount: () => {
                  onValuesChange?.()
                },
              }}
            >
              {_field => (
                <OrderSubstancesForm
                  title={t('trade_name')}
                  form={group}
                  fields={`activeSubstances`}
                  openAddBatchDialog={() => {
                    openAddBatchDialog({
                      prescribedNameIds,
                      onSelectBatch: batchId => {
                        addTradeNameBatch('activeSubstances', batchId)
                        onValuesChange?.()
                      },
                    })
                  }}
                  batches={batches}
                  addBatch={addBatch}
                  prescribedNameIds={prescribedNameIds}
                  onValuesChange={onValuesChange}
                  onRemoveBatch={batchId => {
                    onRemoveBatch?.(batchId, 'activeSubstances')
                    onValuesChange?.()
                  }}
                  onIsolateBatch={batchId => {
                    onIsolateBatch?.(batchId, 'activeSubstances')
                    onValuesChange?.()
                  }}
                  notEnoughAmount={
                    hasReturn ? [] : activeSubstancesOutOfStock.filter(item => !inactiveTradeNames.includes(item.name))
                  }
                  inactiveTradeNames={inactiveTradeNames}
                  batchErrors={batchErrors}
                  restVials={restVials}
                  onRemoveRestVial={restVialId => {
                    group.setFieldValue(
                      'restVials',
                      restVials.filter(rv => rv.restVialId !== restVialId)
                    )
                    onValuesChange?.()
                  }}
                />
              )}
            </group.Field>

            {carrierSolutionId && (
              <group.Field
                name="carrier"
                listeners={{
                  onMount: () => {
                    onValuesChange?.()
                  },
                }}
              >
                {_field => (
                  <OrderSubstancesForm
                    title={t('carrier_solution')}
                    form={group}
                    fields={`carrier`}
                    openAddBatchDialog={() => {
                      openAddBatchDialog({
                        drugCatalogItemIds: [carrierSolutionId],
                        onSelectBatch: batchId => {
                          addTradeNameBatch('carrier', batchId)
                          onValuesChange?.()
                        },
                      })
                    }}
                    batches={batches}
                    addBatch={addBatch}
                    drugCatalogItemIds={[carrierSolutionId]}
                    onValuesChange={onValuesChange}
                    onRemoveBatch={batchId => {
                      onRemoveBatch?.(batchId, 'carrier')
                      onValuesChange?.()
                    }}
                    onIsolateBatch={batchId => {
                      onIsolateBatch?.(batchId, 'carrier')
                      onValuesChange?.()
                    }}
                    notEnoughAmount={hasReturn ? [] : carrierOutOfStock}
                    helperText={carrierHelperText}
                    batchErrors={batchErrors}
                  />
                )}
              </group.Field>
            )}

            {needReconstitution && skipSolventStockCheck && (
              <SolventRequirementsList requirements={solventRequirements} />
            )}

            {needReconstitution && !skipSolventStockCheck && (
              <group.Field
                name="solvents"
                listeners={{
                  onMount: () => {
                    onValuesChange?.()
                  },
                }}
              >
                {_field => (
                  <OrderSubstancesForm
                    title={t('solvents')}
                    form={group}
                    fields={`solvents`}
                    openAddBatchDialog={() => {
                      openAddBatchDialog({
                        drugCatalogItemIds: allowedSolvents,
                        onSelectBatch: batchId => {
                          addTradeNameBatch('solvents', batchId)
                          onValuesChange?.()
                        },
                      })
                    }}
                    batches={batches}
                    addBatch={addBatch}
                    drugCatalogItemIds={allowedSolvents}
                    onValuesChange={onValuesChange}
                    onRemoveBatch={batchId => {
                      onRemoveBatch?.(batchId, 'solvents')
                      onValuesChange?.()
                    }}
                    onIsolateBatch={batchId => {
                      onIsolateBatch?.(batchId, 'solvents')
                      onValuesChange?.()
                    }}
                    notEnoughAmount={hasReturn ? [] : solventOutOfStock}
                    missingTradeNames={hasReturn ? [] : solventsWithoutTradeName}
                    batchErrors={batchErrors}
                    isSolvent
                  />
                )}
              </group.Field>
            )}

            <group.Field
              name="consumables"
              listeners={{
                onMount: () => {
                  onValuesChange?.()
                },
              }}
            >
              {_field => (
                <OrderSubstancesForm
                  title={t('consumables')}
                  form={group}
                  fields={`consumables`}
                  openAddBatchDialog={() => {
                    openAddBatchDialog({
                      isConsumable: true,
                      onSelectBatch: (batchId, consumableId) => {
                        addTradeNameBatch('consumables', batchId, consumableId)
                        onValuesChange?.()
                      },
                    })
                  }}
                  batches={batches}
                  addBatch={addBatch}
                  notEnoughAmount={hasReturn ? [] : consumableOutOfStock}
                  batchErrors={batchErrors}
                  onValuesChange={onValuesChange}
                />
              )}
            </group.Field>
          </Box>
        </Collapsible.Content>
      </Collapsible.Root>
    )
  },
})
